/**
 * useAdminFinancial - Hook para buscar dados financeiros do painel Admin
 * Consulta tenants com planos e addons para calcular MRR (Monthly Recurring Revenue)
 * Inclui integração com Stripe para valores reais cobrados
 */

import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';

/** Tipagem para dados de tenant com plano */
export interface TenantWithPlan {
  tenant_id: string;
  tenant_name: string;
  plan_id: string | null;
  plan_name: string | null;
  price_monthly: number;
  subscription_status: string;
  billing_period: string | null;
  stripe_subscription_id: string | null;
  /** ID do customer no Stripe para consulta de TODAS subscriptions */
  stripe_customer_id: string | null;
}

/** Tipagem para dados de addon ativo */
export interface ActiveAddon {
  tenant_id: string;
  addon_name: string;
  slug: string;
  price_monthly: number;
  quantity: number;
}

/** Tipagem para receita agrupada por plano */
export interface PlanRevenue {
  plan_name: string;
  price_monthly: number;
  tenant_count: number;
  subtotal: number;
  percentage: number;
}

/** Tipagem para receita agrupada por addon */
export interface AddonRevenue {
  addon_name: string;
  slug: string;
  price_monthly: number;
  total_quantity: number;
  subtotal: number;
  percentage: number;
}

/** Tipagem para dados do Stripe por tenant (soma de TODAS subscriptions) */
export interface StripeSubscriptionData {
  total_amount: number;
  subscriptions_count: number;
  discount_percent: number;
  coupon_name: string | null;
  items: { description: string; amount: number; quantity: number }[];
}

/** Tipagem para detalhamento por tenant */
export interface TenantDetail {
  tenant_id: string;
  tenant_name: string;
  plan_name: string | null;
  plan_value: number;
  addons: string[];
  addons_value: number;
  mrr: number;
  status: string;
  billing_period: string | null;
  /** Valor real cobrado no Stripe (em centavos, convertido para BRL) */
  stripe_amount: number | null;
  /** Percentual de desconto aplicado no Stripe */
  discount_percent: number | null;
  /** Nome do cupom aplicado no Stripe */
  coupon_name: string | null;
}

/** Retorno completo do hook */
export interface AdminFinancialData {
  // KPIs
  mrrTotal: number;
  mrrPlans: number;
  mrrAddons: number;
  payingTenants: number;
  averageTicket: number;
  revenueAtRisk: number;
  /** MRR real cobrado no Stripe (com descontos aplicados) */
  mrrStripe: number | null;
  /** Desconto total (diferença entre MRR tabela e MRR Stripe) */
  discountTotal: number | null;
  // Tabelas agrupadas (somente ativos)
  planRevenues: PlanRevenue[];
  addonRevenues: AddonRevenue[];
  // Tabelas agrupadas (todos: active + trialing + past_due)
  allPlanRevenues: PlanRevenue[];
  allAddonRevenues: AddonRevenue[];
  mrrPlansAll: number;
  mrrAddonsAll: number;
  tenantDetails: TenantDetail[];
  // Estado
  isLoading: boolean;
  error: Error | null;
  /** Indica se os dados do Stripe estão carregando separadamente */
  isLoadingStripe: boolean;
}

/** Busca tenants com status de assinatura relevante */
const fetchTenantPlans = async (): Promise<TenantWithPlan[]> => {
  const { data, error } = await supabase
    .from('tenants')
    .select(`
      id,
      name,
      plan,
      subscription_status,
      billing_period,
      stripe_subscription_id,
      stripe_customer_id
    `)
    .in('subscription_status', ['active', 'trialing', 'past_due']);

  if (error) {
    logger.error('[useAdminFinancial] Erro ao buscar tenants:', error.message);
    throw error;
  }

  // Busca planos separadamente para evitar tipo excessivamente profundo
  const tenantPlanIds = [...new Set((data || []).map((t: any) => t.plan).filter(Boolean))];
  const { data: plans } = await supabase
    .from('subscription_plans')
    .select('id, name, price_monthly')
    .in('id', tenantPlanIds.length > 0 ? tenantPlanIds : ['__none__']);

  const planMap = new Map((plans || []).map((p: any) => [p.id, p]));

  return (data || []).map((t: any) => {
    const plan = planMap.get(t.plan);
    return {
      tenant_id: t.id,
      tenant_name: t.name || 'Sem nome',
      plan_id: t.plan,
      plan_name: plan?.name || null,
      price_monthly: plan?.price_monthly || 0,
      subscription_status: t.subscription_status,
      billing_period: t.billing_period,
      stripe_subscription_id: t.stripe_subscription_id || null,
      stripe_customer_id: t.stripe_customer_id || null,
    };
  });
};

/** Busca addons ativos de todos os tenants */
const fetchActiveAddons = async (): Promise<ActiveAddon[]> => {
  const { data, error } = await supabase
    .from('tenant_addons')
    .select('tenant_id, quantity, status, addon_id')
    .eq('status', 'active');

  if (error) {
    logger.error('[useAdminFinancial] Erro ao buscar addons:', error.message);
    throw error;
  }

  // Busca addons separadamente para evitar tipo excessivamente profundo
  const addonIds = [...new Set((data || []).map((a: any) => a.addon_id).filter(Boolean))];
  const { data: addonsData } = await supabase
    .from('plan_addons')
    .select('id, name, price_monthly, slug')
    .in('id', addonIds.length > 0 ? addonIds : ['__none__']);

  const addonInfoMap = new Map((addonsData || []).map((a: any) => [a.id, a]));

  return (data || []).map((a: any) => {
    const info = addonInfoMap.get(a.addon_id);
    return {
      tenant_id: a.tenant_id,
      addon_name: info?.name || 'Addon',
      slug: info?.slug || '',
      price_monthly: info?.price_monthly || 0,
      quantity: a.quantity || 1,
    };
  });
};

/** Busca valores reais do Stripe via Edge Function (por customer_id) */
const fetchStripeData = async (
  customers: { tenant_id: string; customer_id: string }[]
): Promise<Record<string, StripeSubscriptionData>> => {
  if (customers.length === 0) return {};

  const { data, error } = await supabase.functions.invoke('admin-financial-stripe', {
    body: { customers },
  });

  if (error) {
    logger.error('[useAdminFinancial] Erro ao consultar Stripe:', error.message);
    throw error;
  }

  // Resposta indexada por tenant_id
  return data?.tenants || {};
};

/** Hook principal para dados financeiros do Admin */
export function useAdminFinancial(): AdminFinancialData {
  // Query de tenants com planos
  const tenantsQuery = useQuery({
    queryKey: ['admin-financial-tenants'],
    queryFn: fetchTenantPlans,
    staleTime: 2 * 60 * 1000,
  });

  // Query de addons ativos
  const addonsQuery = useQuery({
    queryKey: ['admin-financial-addons'],
    queryFn: fetchActiveAddons,
    staleTime: 2 * 60 * 1000,
  });

  const tenants = tenantsQuery.data || [];
  const addons = addonsQuery.data || [];

  // Monta array de customers para consulta no Stripe (tenant_id + customer_id)
  const stripeCustomers = useMemo(
    () => tenants
      .filter(t => Boolean(t.stripe_customer_id))
      .map(t => ({ tenant_id: t.tenant_id, customer_id: t.stripe_customer_id! })),
    [tenants]
  );

  // Query de dados do Stripe (depende dos tenants estarem carregados)
  const stripeQuery = useQuery({
    queryKey: ['admin-financial-stripe', stripeCustomers],
    queryFn: () => fetchStripeData(stripeCustomers),
    enabled: stripeCustomers.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutos (dados do Stripe mudam menos)
  });

  const stripeData = stripeQuery.data || {};

  // Mapa de tenant_id -> dados Stripe (já vem indexado por tenant_id)
  const stripeByTenant = useMemo(() => {
    const map = new Map<string, StripeSubscriptionData>();
    Object.entries(stripeData).forEach(([tenantId, data]) => {
      map.set(tenantId, data as StripeSubscriptionData);
    });
    return map;
  }, [stripeData]);

  // Cálculos de MRR com useMemo para performance
  const financialData = useMemo(() => {
    // Tenants ativos (excluindo past_due para MRR principal)
    const activeTenants = tenants.filter(t => t.subscription_status === 'active' || t.subscription_status === 'trialing');
    const pastDueTenants = tenants.filter(t => t.subscription_status === 'past_due');

    // MRR de Planos
    const mrrPlans = activeTenants.reduce((acc, t) => acc + t.price_monthly, 0);

    // MRR de Addons (filtra apenas addons de tenants ativos)
    const activeTenantIds = new Set(activeTenants.map(t => t.tenant_id));
    const activeAddonsList = addons.filter(a => activeTenantIds.has(a.tenant_id));
    const mrrAddons = activeAddonsList.reduce((acc, a) => acc + (a.price_monthly * a.quantity), 0);

    // MRR Total
    const mrrTotal = mrrPlans + mrrAddons;

    // Tenants pagantes (com plano ativo e preço > 0)
    const payingTenants = activeTenants.filter(t => t.price_monthly > 0).length;

    // Ticket médio
    const averageTicket = payingTenants > 0 ? mrrTotal / payingTenants : 0;

    // Receita em risco (past_due)
    const revenueAtRisk = pastDueTenants.reduce((acc, t) => acc + t.price_monthly, 0);

    // MRR Real (Stripe) - soma dos valores reais cobrados
    let mrrStripe: number | null = null;
    if (Object.keys(stripeData).length > 0) {
      mrrStripe = 0;
      activeTenants.forEach(t => {
        const stripe = stripeByTenant.get(t.tenant_id);
        if (stripe && stripe.total_amount >= 0) {
          mrrStripe! += stripe.total_amount / 100; // Centavos para reais
        }
      });
    }

    // Desconto total (diferença entre MRR tabela e MRR Stripe)
    const discountTotal = mrrStripe !== null ? mrrTotal - mrrStripe : null;

    // Receita por plano (agrupada) - somente ativos
    const planMap = new Map<string, { price: number; count: number }>();
    activeTenants.forEach(t => {
      const name = t.plan_name || 'Sem plano';
      const existing = planMap.get(name) || { price: t.price_monthly, count: 0 };
      existing.count += 1;
      planMap.set(name, existing);
    });
    const planRevenues: PlanRevenue[] = Array.from(planMap.entries())
      .map(([plan_name, data]) => ({
        plan_name,
        price_monthly: data.price,
        tenant_count: data.count,
        subtotal: data.price * data.count,
        percentage: mrrPlans > 0 ? ((data.price * data.count) / mrrPlans) * 100 : 0,
      }))
      .sort((a, b) => b.subtotal - a.subtotal);

    // Receita por plano - TODOS (active + trialing + past_due)
    const allPlanMap = new Map<string, { price: number; count: number }>();
    tenants.forEach(t => {
      const name = t.plan_name || 'Sem plano';
      const existing = allPlanMap.get(name) || { price: t.price_monthly, count: 0 };
      existing.count += 1;
      allPlanMap.set(name, existing);
    });
    const mrrPlansAll = tenants.reduce((acc, t) => acc + t.price_monthly, 0);
    const allPlanRevenues: PlanRevenue[] = Array.from(allPlanMap.entries())
      .map(([plan_name, data]) => ({
        plan_name,
        price_monthly: data.price,
        tenant_count: data.count,
        subtotal: data.price * data.count,
        percentage: mrrPlansAll > 0 ? ((data.price * data.count) / mrrPlansAll) * 100 : 0,
      }))
      .sort((a, b) => b.subtotal - a.subtotal);

    // Receita por addon (agrupada) - somente ativos
    const addonMap = new Map<string, { slug: string; price: number; qty: number }>();
    activeAddonsList.forEach(a => {
      const existing = addonMap.get(a.addon_name) || { slug: a.slug, price: a.price_monthly, qty: 0 };
      existing.qty += a.quantity;
      addonMap.set(a.addon_name, existing);
    });
    const addonRevenues: AddonRevenue[] = Array.from(addonMap.entries())
      .map(([addon_name, data]) => ({
        addon_name,
        slug: data.slug,
        price_monthly: data.price,
        total_quantity: data.qty,
        subtotal: data.price * data.qty,
        percentage: mrrAddons > 0 ? ((data.price * data.qty) / mrrAddons) * 100 : 0,
      }))
      .sort((a, b) => b.subtotal - a.subtotal);

    // Receita por addon - TODOS os tenants
    const allTenantIds = new Set(tenants.map(t => t.tenant_id));
    const allAddonsList = addons.filter(a => allTenantIds.has(a.tenant_id));
    const mrrAddonsAll = allAddonsList.reduce((acc, a) => acc + (a.price_monthly * a.quantity), 0);
    const allAddonMap = new Map<string, { slug: string; price: number; qty: number }>();
    allAddonsList.forEach(a => {
      const existing = allAddonMap.get(a.addon_name) || { slug: a.slug, price: a.price_monthly, qty: 0 };
      existing.qty += a.quantity;
      allAddonMap.set(a.addon_name, existing);
    });
    const allAddonRevenues: AddonRevenue[] = Array.from(allAddonMap.entries())
      .map(([addon_name, data]) => ({
        addon_name,
        slug: data.slug,
        price_monthly: data.price,
        total_quantity: data.qty,
        subtotal: data.price * data.qty,
        percentage: mrrAddonsAll > 0 ? ((data.price * data.qty) / mrrAddonsAll) * 100 : 0,
      }))
      .sort((a, b) => b.subtotal - a.subtotal);

    // Detalhamento por tenant
    const tenantAddonMap = new Map<string, { names: string[]; total: number }>();
    addons.forEach(a => {
      const existing = tenantAddonMap.get(a.tenant_id) || { names: [], total: 0 };
      existing.names.push(`${a.addon_name} (x${a.quantity})`);
      existing.total += a.price_monthly * a.quantity;
      tenantAddonMap.set(a.tenant_id, existing);
    });

    const tenantDetails: TenantDetail[] = tenants
      .filter(t => t.price_monthly > 0 || tenantAddonMap.has(t.tenant_id))
      .map(t => {
        const addonData = tenantAddonMap.get(t.tenant_id) || { names: [], total: 0 };
        const stripeInfo = stripeByTenant.get(t.tenant_id);
        return {
          tenant_id: t.tenant_id,
          tenant_name: t.tenant_name,
          plan_name: t.plan_name,
          plan_value: t.price_monthly,
          addons: addonData.names,
          addons_value: addonData.total,
          mrr: t.price_monthly + addonData.total,
          status: t.subscription_status,
          billing_period: t.billing_period,
          stripe_amount: stripeInfo ? stripeInfo.total_amount / 100 : null,
          discount_percent: stripeInfo ? stripeInfo.discount_percent : null,
          coupon_name: stripeInfo?.coupon_name || null,
        };
      })
      .sort((a, b) => b.mrr - a.mrr);

    return {
      mrrTotal,
      mrrPlans,
      mrrAddons,
      payingTenants,
      averageTicket,
      revenueAtRisk,
      mrrStripe,
      discountTotal,
      planRevenues,
      addonRevenues,
      allPlanRevenues,
      allAddonRevenues,
      mrrPlansAll,
      mrrAddonsAll,
      tenantDetails,
    };
  }, [tenants, addons, stripeData, stripeByTenant]);

  return {
    ...financialData,
    isLoading: tenantsQuery.isLoading || addonsQuery.isLoading,
    isLoadingStripe: stripeQuery.isLoading,
    error: tenantsQuery.error || addonsQuery.error || null,
  };
}
